import { useMutationWithToast } from '@pesto/ui';
import { Button, Typography } from '@pesto/ui';
import { PauseCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { ModalProps, ProgramModalPayload } from '../../types/modal.types';

import { ListProgramsDocument, useRemoveProgramMutation } from '../../../__generated__/graphql/api';
import { routes } from '../../../constants/routes';

export const RemoveProgramComponent = ({ payload, api }: ModalProps<ProgramModalPayload, true>) => {
  const { programId, programName } = payload;
  const navigate = useNavigate();
  const { handleMutation } = useMutationWithToast();

  const [removeProgram, { loading }] = useRemoveProgramMutation({
    refetchQueries: [{ query: ListProgramsDocument }],
  });

  const handleConfirmRemove = () => {
    void handleMutation({
      mutation: () => removeProgram({ variables: { programId } }),
      extractSuccess: (result) => !!result?.data?.removeProgram,
      debugLabel: 'deactivate-program',
      toast: {
        success: 'Program deactivated successfully',
        error: 'Failed to deactivate program',
        loading: 'Deactivating program...',
      },
      onSuccess: () => {
        api.close(true);
        navigate(routes.programs);
      },
    });
  };

  const handleCancel = () => {
    api.cancel();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="bg-muted/10 flex h-12 w-12 items-center justify-center rounded-full">
          <PauseCircle className="text-primary h-6 w-6" />
        </div>
        <div>
          <Typography variant="headerSmall" className="font-semibold">
            Deactivate Program
          </Typography>
          <Typography variant="bodySmall" className="text-muted-foreground">
            You can reactivate this program at any time
          </Typography>
        </div>
      </div>

      <div className="space-y-3">
        <Typography variant="body">Are you sure you want to deactivate this program?</Typography>

        <div className="bg-muted/50 rounded-lg border p-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <Typography variant="bodySmall" className="text-muted-foreground">
                Program Name:
              </Typography>
              <Typography variant="bodySmall" className="font-medium">
                {programName}
              </Typography>
            </div>
            <div className="flex justify-between">
              <Typography variant="bodySmall" className="text-muted-foreground">
                Program ID:
              </Typography>
              <Typography variant="bodySmall" className="font-mono font-medium">
                {programId}
              </Typography>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-warning/20 bg-warning/10 p-4">
          <Typography variant="bodySmall" className="text-warning">
            <strong>Note:</strong> Deactivating this program will pause its activity and hide it from use. No data will
            be lost and you can reactivate it later.
          </Typography>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirmRemove} loading={loading}>
          Deactivate Program
        </Button>
      </div>
    </div>
  );
};
